<script lang="ts">import { goto } from "$app/navigation";
import { Button } from "@infinitro/button";
import { favoriteGame, favoriteGames, isFavoriteGame, isLikedGame, likedGames, likeGame, refreshGamesState } from "@infinitro/catalog-ex";
import { hasGtDemo, toGameSlug, toProviderSlug } from "@infinitro/catalog-min";
import { cfgBrandTitle, cfgCdnTiles, cfgToThemeCdnPath, cfgToThemeContentPath, FeatureSet, gameDemoMode, ReleaseStage, stateFeatureSet, stateReleaseStage, stateUi } from "@infinitro/config";
import { t, Verbiage } from "@infinitro/content";
import { gameLaunchedFromPath, isMobile, locale, loggedIn, ModalDialog, providerBasePath, pushModal, selectedGameInfo, stateOpenModal, stateRealm, toSiteUrl } from "@infinitro/global";
import { ArrowIcon, CloseIcon } from "@infinitro/icons";
import { InfoCard } from "@infinitro/info-card";
import { Tabs } from "@infinitro/tabs";
import { Text } from "@infinitro/text";
import MarkdownIt from "markdown-it";
import { onMount } from "svelte";
import { get } from "svelte/store";
import AverageRating from "./parts/review/average-rating.svelte";
import Comments from "./parts/review/comments.svelte";
import RateGame from "./parts/review/rate-game.svelte";
import { isProviderFirstLocale } from "./utils/provider-attribution";
const CDN_TILES = cfgCdnTiles();
const md = new MarkdownIt();
$: gameData = $selectedGameInfo;
$: game = gameData?.game;
$: tileSrc = gameData?.tileSrc;
let content;
let htmlContent = "";
let fetchContentError = false;
let activeTab = "description";
let hasDescription = false;
let reviewRefreshKey = 0;
let liked = false;
let favourited = false;
let favouritesCount = 0;
let likesCount = 0;
let favLikeCountsGameKey = "";
let realm;
stateRealm.subscribe((value) => realm = value);
$: {
	const key = game && (game.provider_slug || game.prov) && (game.game_slug || game.game) ? `${String(game.provider_slug || game.prov)}:${String(game.game_slug || game.game)}` : "";
	if (key && key !== favLikeCountsGameKey) {
		favLikeCountsGameKey = key;
		favouritesCount = 0;
		likesCount = 0;
	}
}
$: (async () => {
	if ($favoriteGames && game) {
		favourited = await isFavoriteGame(game, { realm }, !!$loggedIn);
	} else {
		favourited = false;
	}
})();
$: (async () => {
	if ($likedGames && game) {
		liked = await isLikedGame(game, { realm }, !!$loggedIn);
	} else {
		liked = false;
	}
})();
async function handleFavouriteClick() {
	if (!$loggedIn) return;
	try {
		const provSlug = game.provider_slug || game.prov || "";
		const gSlug = game.game_slug || game.game;
		await favoriteGame(provSlug, gSlug, !!$loggedIn);
		favourited = !favourited;
		if (favourited) {
			favouritesCount += 1;
		} else {
			favouritesCount = Math.max(0, favouritesCount - 1);
		}
		await refreshGamesState();
	} catch (error) {
		console.log("Error favoriting game:", error);
	}
}
async function handleLikeClick() {
	if (!$loggedIn) return;
	try {
		const provSlug = game.provider_slug || game.prov || "";
		const gSlug = game.game_slug || game.game;
		await likeGame(provSlug, gSlug, !!$loggedIn);
		liked = !liked;
		if (liked) {
			likesCount += 1;
		} else {
			likesCount = Math.max(0, likesCount - 1);
		}
		await refreshGamesState();
	} catch (error) {
		console.log("Error liking game:", error);
	}
}
$: useGamePathInfoModalFlow = $isMobile && $stateUi.games.showGameInfoModalOnGamePathMobile;
/**
* Reviews / ratings / comments / like & fav counts: Alpha release + Preview feature set only.
* Must read `$stateReleaseStage` (not only `wantAtLeastReleaseStage`) so the UI updates when
* dev settings change release — `wantAtLeastReleaseStage` uses an internal snapshot Svelte
* does not subscribe to.
*/
$: isGameInfoReviewsPreview = $stateReleaseStage === ReleaseStage.Alpha && $stateFeatureSet === FeatureSet.Preview;
$: gameInfoModalTabs = isGameInfoReviewsPreview && hasDescription ? [{
	label: $t(Verbiage.description) || "Description",
	value: "description"
}, {
	label: $t(Verbiage.reviews_tab) || "Review",
	value: "review"
}] : [];
/** Open only when there is markdown, or Preview (reviews) is enabled — same idea as desktop `info-accordion`. */
$: canOpenDescriptionAccordion = isGameInfoReviewsPreview || hasDescription;
$: localizedGameName = $locale === "ja" ? game?.ja ?? game?.game ?? "" : $locale === "ko" ? game?.ko ?? game?.game ?? "" : game?.game ?? "";
$: providerNameTrim = game?.prov?.trim() ?? "";
$: providerPathForModal = game?.prov ? `${providerBasePath(game.prov)}${toProviderSlug(game.provider_slug || game.prov)}/` : null;
$: infoAccordionUiModal = $stateUi?.infoAccordion;
$: providerPageHrefModal = !!infoAccordionUiModal?.showProviderLink && providerPathForModal ? toSiteUrl(providerPathForModal, $loggedIn) : null;
function handleProviderPageLinkClick(event) {
	event.preventDefault();
	event.stopPropagation();
	if (!providerPageHrefModal) return;
	goto(providerPageHrefModal);
}
let descriptionDropdownOpen = false;
$: if (!canOpenDescriptionAccordion) {
	descriptionDropdownOpen = false;
}
function toggleDescriptionDropdown() {
	if (!canOpenDescriptionAccordion) return;
	descriptionDropdownOpen = !descriptionDropdownOpen;
}
// Handle review update event from RateGame component
function handleReviewUpdate(event) {
	reviewRefreshKey += 1;
}
function handleGameInfoModalTabChange(detail) {
	activeTab = String(detail.value);
}
async function fetchContent() {
	try {
		const response = await fetch(content);
		if (!response.ok) {
			fetchContentError = true;
			hasDescription = false;
			if (isGameInfoReviewsPreview) {
				activeTab = "review";
			}
			throw new Error(`Error fetching content: ${response.statusText}`);
		}
		let text = await response.text();
		text = text.replace(/%(domain|brand)%/g, cfgBrandTitle());
		htmlContent = md.render(text).replace(/<\/?h1(\s|>)/g, (m) => m.replace("h1", "h2"));
		htmlContent = htmlContent.replace(new RegExp("<p>", "g"), "<p style=\"font-weight: 400\">");
		htmlContent = htmlContent.replace(new RegExp("<li>", "g"), "<li style=\"font-weight: 400\">");
		htmlContent = htmlContent.replace(new RegExp("<table>", "g"), "<div class=\"game-info-markdown-table-outer\"><div class=\"game-info-markdown-table-inner\"><table>");
		htmlContent = htmlContent.replace(new RegExp("</table>", "g"), "</table></div></div>");
		hasDescription = true;
		activeTab = "description";
	} catch (error) {
		fetchContentError = true;
		hasDescription = false;
		if (isGameInfoReviewsPreview) {
			activeTab = "review";
		}
		htmlContent = "";
	}
}
$: {
	if (game && game.game) {
		const providerPathSlug = game.provider_slug || toProviderSlug(game.prov ?? "");
		if (providerPathSlug) {
			const mdSlug = game.game_slug || toGameSlug(game.game);
			content = cfgToThemeContentPath(`/seo/games/${providerPathSlug}/${mdSlug}/${mdSlug}.${$locale}.md`);
			fetchContent();
		}
	}
}
function closeModal(clearGameInfo = true) {
	stateOpenModal.set(undefined);
	if (clearGameInfo) {
		selectedGameInfo.set(undefined);
	}
}
function handleSignIn() {
	pushModal(ModalDialog.Auth, { tab: "login" });
}
function getGameUrl() {
	if (!game) return "#";
	const provider = game.prov ?? "";
	const gameSlug = provider.toLowerCase() === "sports" ? game.game : toGameSlug(game.game);
	return toSiteUrl(`${providerBasePath(provider)}${toProviderSlug(provider)}/${gameSlug}`, $loggedIn) || "#";
}
async function handleModalClose() {
	// When the modal was opened from the game path on mobile (brand opt-in), closing it
	// should return the user to where they came from instead of leaving them on the game URL
	// with no game running.
	if (useGamePathInfoModalFlow) {
		const previousPath = get(gameLaunchedFromPath);
		const target = previousPath && previousPath !== window.location.pathname ? previousPath : toSiteUrl("/casino", $loggedIn);
		// Navigate before clearing modal state so the game page does not flash underneath.
		try {
			await goto(target);
		} finally {
			closeModal();
		}
		return;
	}
	closeModal();
}
function shouldLaunchInCurrentPath(url) {
	if (!url || url === "#") return false;
	const launchPath = new URL(url, window.location.origin).pathname;
	return launchPath === window.location.pathname;
}
function handleDemoPlay() {
	gameDemoMode.set(true);
	gameLaunchedFromPath.set(window.location.pathname);
	const url = getGameUrl();
	if (useGamePathInfoModalFlow && shouldLaunchInCurrentPath(url)) {
		selectedGameInfo.update((value) => ({
			...value ?? {},
			launchIntent: "demo"
		}));
		closeModal(false);
		return;
	}
	closeModal();
	if (url !== "#") {
		goto(url);
	}
}
function handleRealPlay() {
	gameDemoMode.set(false);
	gameLaunchedFromPath.set(window.location.pathname);
	const url = getGameUrl();
	if (useGamePathInfoModalFlow && shouldLaunchInCurrentPath(url)) {
		selectedGameInfo.update((value) => ({
			...value ?? {},
			launchIntent: "real"
		}));
		closeModal(false);
		return;
	}
	closeModal();
	if (url !== "#") {
		goto(url);
	}
}
onMount(async () => {
	if (game && game.game) {
		fetchContent();
	}
	if (game && $loggedIn) {
		try {
			favourited = await isFavoriteGame(game, { realm }, !!$loggedIn);
			liked = await isLikedGame(game, { realm }, !!$loggedIn);
		} catch (error) {
			console.log("Error fetching game fav/like state:", error);
		}
	}
});
</script>

{#if game}
	<!-- svelte-ignore a11y-click-events-have-key-events -->
	<!-- svelte-ignore a11y-no-static-element-interactions -->
	<div class="modal-backdrop game-info-modal" on:click={handleModalClose}>
		<div class="modal-container" on:click|stopPropagation>
			<!-- Close Button -->
			<button class="close-button" on:click={handleModalClose} aria-label="Close">
				<CloseIcon />
			</button>

			<!-- Top Section: Game Tile + Info + Buttons -->
			<div class="game-info-section">
				<div class="game-tile-container">
					<img src={tileSrc} alt={game.game} class="game-tile-image" />
				</div>
				<div class="game-details-container">
					<div class="game-text-info">
						<img
							class="game-info-brand-logo"
							src={cfgToThemeCdnPath('/misc/logo.svg')}
							alt={$t(Verbiage.site_logo_alt, 'alts')}
						/>
						<Text type="title" titleSize="h6" titleWeight="semibold"
							>{$locale === 'ja'
								? (game.ja ?? game.game)
								: $locale === 'ko'
									? (game.ko ?? game.game)
									: game.game}</Text
						>
						<Text type="body" textColor="white">
							{#if isProviderFirstLocale($locale)}
								<span class="game-info-provider-name">{game.prov ?? ''}</span>
								<span class="game-info-by-prefix"> {$t(Verbiage.by)}</span>
							{:else}
								<span class="game-info-by-prefix">{$t(Verbiage.by)} </span>
								<span class="game-info-provider-name">{game.prov ?? ''}</span>
							{/if}
						</Text>
					</div>
					<div class="game-action-buttons">
						{#if !$loggedIn}
							<Button
								width="100%"
								variant="primary"
								text={$t(Verbiage.login)}
								size="small"
								onClick={handleSignIn}
							/>
							<Button
								disabled={!game || !game.f || !hasGtDemo(game.f)}
								width="100%"
								variant="secondary"
								text={$t(Verbiage.demo_play)}
								size="small"
								onClick={handleDemoPlay}
							/>
						{:else}
							<Button
								width="100%"
								variant="primary"
								text={$t(Verbiage.play)}
								size="small"
								onClick={handleRealPlay}
							/>
							{#if game && game.f && hasGtDemo(game.f)}
								<Button
									width="100%"
									variant="secondary"
									text={$t(Verbiage.demo_play)}
									size="small"
									onClick={handleDemoPlay}
								/>
							{/if}
						{/if}
					</div>
				</div>
			</div>

			<!-- Bottom Section: Content -->
			<div class="accordion-section">
				{#if $loggedIn}
					<InfoCard
						width="100%"
						noPadding
						borderRadius="8px"
						customStyles="margin-top: 0.5rem; margin-bottom: 0.5rem;"
						customStylesCardContent="padding: 4px 16px 8px;"
					>
						<div class="game-info-settings-group">
							<div class="game-info-like-fav">
								<button
									type="button"
									class="game-info-icon-button"
									on:click={handleFavouriteClick}
									aria-label={$t(Verbiage.favourite)}
								>
									<svg
										width="16"
										height="16"
										viewBox="0 0 16 16"
										fill="none"
										xmlns="http://www.w3.org/2000/svg"
										aria-hidden="true"
									>
										<path
											d="M7.49009 0.332066C7.69867 -0.110689 8.30133 -0.110688 8.50991 0.332067L10.5415 4.64464C10.6244 4.82046 10.7845 4.94232 10.9697 4.97051L15.5125 5.66206C15.9789 5.73306 16.1651 6.33352 15.8277 6.67815L12.5404 10.035C12.4064 10.1719 12.3453 10.3691 12.3769 10.5623L13.1529 15.3023C13.2326 15.7889 12.745 16.16 12.3279 15.9302L8.2646 13.6923C8.09895 13.6011 7.90105 13.6011 7.7354 13.6923L3.67215 15.9302C3.25499 16.16 2.76743 15.7889 2.8471 15.3023L3.62311 10.5623C3.65475 10.3691 3.5936 10.1719 3.45958 10.035L0.172339 6.67815C-0.165149 6.33352 0.0210819 5.73306 0.487479 5.66206L5.03033 4.97051C5.21554 4.94232 5.37564 4.82046 5.45847 4.64464L7.49009 0.332066Z"
											fill={favourited ? '#FF9E14' : 'white'}
										/>
									</svg>
								</button>
								{#if isGameInfoReviewsPreview}
									<span class="game-info-fav-like-count">{favouritesCount}</span>
								{/if}
							</div>
							{#if $stateUi.games?.showLikeButton}
								<div class="game-info-like-fav">
									<button
										type="button"
										class="game-info-icon-button"
										on:click={handleLikeClick}
										aria-label={$t(Verbiage.like)}
									>
										<svg
											width="18"
											height="16"
											viewBox="0 0 18 16"
											fill="none"
											xmlns="http://www.w3.org/2000/svg"
											aria-hidden="true"
										>
											<path
												d="M12.996 0C11.367 0 9.909 0.791011 9 2.00449C8.091 0.791011 6.633 0 5.004 0C2.241 0 0 2.24719 0 5.02472C0 6.09438 0.171 7.08315 0.468 8C1.89 12.4944 6.273 15.182 8.442 15.9191C8.748 16.027 9.252 16.027 9.558 15.9191C11.727 15.182 16.11 12.4944 17.532 8C17.829 7.08315 18 6.09438 18 5.02472C18 2.24719 15.759 0 12.996 0Z"
												fill={liked ? '#FF375F' : 'white'}
											/>
										</svg>
									</button>
									{#if isGameInfoReviewsPreview}
										<span class="game-info-fav-like-count">{likesCount}</span>
									{/if}
								</div>
							{/if}
						</div>
					</InfoCard>
				{/if}

				<InfoCard
					width="100%"
					borderRadius="12px"
					noPadding
					customStyles="margin-top: 0.5rem; margin-bottom: 1rem;"
					customStylesCardContent="padding: 0;"
				>
					<!-- svelte-ignore a11y-click-events-have-key-events -->
					<!-- svelte-ignore a11y-no-static-element-interactions -->
					<div class="gi-info-dropdown {descriptionDropdownOpen ? 'gi-info-dropdown-open' : ''}">
						<div
							class="gi-info-dropdown-toggle"
							class:pointer={canOpenDescriptionAccordion}
							on:click|stopPropagation={toggleDescriptionDropdown}
						>
							<div class="gi-info-dropdown-titles">
								<Text type="title" titleSize="h6" titleWeight="semibold">{localizedGameName}</Text>
								{#if providerNameTrim}
									<Text type="body" textColor="white">
										{#if isProviderFirstLocale($locale)}
											{#if providerPageHrefModal}
												<a
													href={providerPageHrefModal}
													class="game-info-provider-name gi-provider-page-link"
													rel="noopener noreferrer"
													on:click={handleProviderPageLinkClick}
												>
													{providerNameTrim}
												</a>
											{:else}
												<span class="game-info-provider-name">{providerNameTrim}</span>
											{/if}
											<span class="game-info-by-prefix"> {$t(Verbiage.by)}</span>
										{:else}
											<span class="game-info-by-prefix">{$t(Verbiage.by)} </span>
											{#if providerPageHrefModal}
												<a
													href={providerPageHrefModal}
													class="game-info-provider-name gi-provider-page-link"
													rel="noopener noreferrer"
													on:click={handleProviderPageLinkClick}
												>
													{providerNameTrim}
												</a>
											{:else}
												<span class="game-info-provider-name">{providerNameTrim}</span>
											{/if}
										{/if}
									</Text>
								{/if}
							</div>
							{#if canOpenDescriptionAccordion}
								<div class="gi-info-chevron" class:rotated={descriptionDropdownOpen}>
									<ArrowIcon />
								</div>
							{/if}
						</div>
						<div class="gi-info-dropdown-menu" class:expanded={descriptionDropdownOpen}>
							<div class="gi-info-dropdown-inner">
								<div class="game-description">
									{#if isGameInfoReviewsPreview && hasDescription}
										<Tabs
											tabs={gameInfoModalTabs}
											{activeTab}
											onChange={handleGameInfoModalTabChange}
											width="100%"
											size="small"
											shouldScrollIntoView={false}
											customClass="game-info-modal-tab-strip"
										/>
										{#if activeTab === 'description'}
											{#if htmlContent}
												<div class="game-info-markdown">{@html htmlContent}</div>
											{/if}
										{:else if activeTab === 'review'}
											<div class="review-content">
												<InfoCard
													width="100%"
													borderRadius="8px"
													flatBackgroundColor="var(--color-background-100)"
													noBorders
												>
													<div class="review-cards-row">
														<RateGame {game} on:reviewUpdated={handleReviewUpdate} />
														{#key reviewRefreshKey}
															<AverageRating {game} />
														{/key}
													</div>
												</InfoCard>
												<Comments
													gameSlug={toGameSlug(game.game)}
													providerSlug={game.provider_slug ?? ''}
												/>
											</div>
										{/if}
									{:else if isGameInfoReviewsPreview && !hasDescription}
										<div class="review-content">
											<InfoCard width="100%" borderRadius="8px">
												<div class="review-cards-row">
													<RateGame {game} on:reviewUpdated={handleReviewUpdate} />
													{#key reviewRefreshKey}
														<AverageRating {game} />
													{/key}
												</div>
											</InfoCard>
											<Comments
												gameSlug={toGameSlug(game.game)}
												providerSlug={game.provider_slug ?? ''}
											/>
										</div>
									{:else if hasDescription && htmlContent}
										<div class="game-info-markdown">{@html htmlContent}</div>
									{/if}
								</div>
							</div>
						</div>
					</div>
				</InfoCard>
			</div>
		</div>
	</div>
{/if}

<style>
	@keyframes slideUp {
		from {
			transform: translateY(100%);
			opacity: 0;
		}
		to {
			transform: translateY(0);
			opacity: 1;
		}
	}

	.game-info-modal {
		display: block;
}

	@media (min-width: 1024px) {
		.game-info-modal {
				display: none;
		}
}

	.modal-backdrop {
		/* Below default overlay (9999) so stacked modals (e.g. Auth) render on top — see modals.svelte DOM order */
		position: fixed;
		inset: 0px;
		z-index: 9998;
		display: flex;
		align-items: flex-end;
		justify-content: center;
		background-color: rgb(0 0 0 / var(--tw-bg-opacity, 1));
		--tw-bg-opacity: 0.5;
	}

	@media (min-width: 1024px) {
		.modal-backdrop {
				align-items: center;
		}
	}

	.modal-container {
		position: relative;
		display: flex;
		height: 100%;
		width: 100%;
		flex-direction: column;
		overflow-y: auto;
		border-width: 1.5px;
		border-color: var(--color-background-300);
		font-size: 0.875rem;
		line-height: 1.25rem;
		color: var(--color-text-100);
		--tw-shadow: 0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1);
		--tw-shadow-colored: 0 1px 3px 0 var(--tw-shadow-color), 0 1px 2px -1px var(--tw-shadow-color);
		box-shadow: var(--tw-ring-offset-shadow, 0 0 #0000), var(--tw-ring-shadow, 0 0 #0000), var(--tw-shadow);
		font-family: var(--font-main);
		background-image:
			radial-gradient(circle at 50% 0%, var(--light-color), transparent 50%),
			radial-gradient(circle at 50% 100%, var(--light-color), transparent 50%),
			linear-gradient(to bottom, var(--color-background-700), var(--color-background-700)),
			linear-gradient(to bottom, rgba(255, 255, 255, 0.22), var(--color-neutral-900));
		background-origin: border-box;
		background-clip: content-box, border-box;
		border: double 1px transparent;
		box-shadow: 0 0 0 2px var(--color-neutral-900) !important;
		--light-color: color-mix(in srgb, var(--color-secondary-700) 50%, transparent);
		--light-color: rgb(from var(--color-secondary-700) r g b / 0.5);
		animation: slideUp 0.5s ease-out;
		min-height: 100vh;
		min-height: 100dvh;
}

	@media (min-width: 1024px) {
		.modal-container {
				border-radius: 24px;
			max-width: 90vw;
			max-height: 95vh;
			min-height: 0;
			width: 800px;
		}
	}

	.close-button {
		position: absolute;
		top: 0px;
		right: 0px;
		z-index: 150;
		margin: 1rem;
		display: flex;
		height: 24px;
		width: 24px;
		align-items: center;
		justify-content: center;
		border-radius: 8px;
		border-top-width: 1px;
		border-color: #3F4B7980;
		font-size: 1rem;
		line-height: 1.5rem;
		font-weight: 500;
		--tw-text-opacity: 1;
		color: rgb(209 213 219 / var(--tw-text-opacity, 1));
		background-image: linear-gradient(
			180deg,
			var(--color-background-300) 0%,
			var(--color-background-700) 100%
		);
}

	.close-button:hover {
		--tw-border-opacity: 1;
		border-color: rgb(156 163 175 / var(--tw-border-opacity, 1));
		--tw-text-opacity: 1;
		color: rgb(229 231 235 / var(--tw-text-opacity, 1));
}

	.game-info-section {
		display: grid;
		grid-template-columns: repeat(2, minmax(0, 1fr));
		align-items: flex-start;
		gap: 1rem;
		padding: 1rem;
}

	.game-tile-container {
		aspect-ratio: 3/4;
		width: 100%;
		min-width: 0px;
		overflow: hidden;
		border-radius: 0.5rem;
}

	.game-tile-image {
		height: 100%;
		width: 100%;
		-o-object-fit: cover;
		   object-fit: cover;
}

	.game-details-container {
		display: flex;
		width: 100%;
		min-width: 0px;
		flex-direction: column;
		justify-content: space-between;
		gap: 0.75rem;
}

	.game-text-info {
		display: flex;
		flex-direction: column;
		gap: 0.25rem;
}

	.game-info-brand-logo {
		display: block;
		height: 14px;
		width: auto;
		max-width: 100%;
		-o-object-fit: contain;
		   object-fit: contain;
		-o-object-position: left center;
		   object-position: left center;
		margin-bottom: 2px;
		opacity: 0.5;
	}

	.game-action-buttons {
		display: flex;
		flex-direction: column;
		gap: 0.5rem;
}

	.accordion-section {
		flex: 1 1 0%;
		overflow-y: auto;
		padding-left: 1rem;
		padding-right: 1rem;
}

	.gi-info-dropdown {
		position: relative;
		height: -moz-fit-content;
		height: fit-content;
		width: 100%;
		border-radius: 0.75rem;
		border-width: 1px;
		border-color: var(--color-neutral-900);
		background: var(--gradient-main);
}

	/* 	.gi-info-dropdown-open {
		@apply [background:var(--gradient-alternative)];
	} */

	.gi-info-dropdown-toggle {
		position: relative;
		z-index: 10;
		display: block;
		min-height: 88px;
		width: 100%;
		border-radius: 0.5rem;
		padding: 1rem;
		--tw-text-opacity: 1;
		color: rgb(255 255 255 / var(--tw-text-opacity, 1));
		font-family: var(--font-main);
		display: flex;
		align-items: center;
		justify-content: space-between;
}

	.pointer {
		cursor: pointer;
}

	.gi-info-dropdown-titles {
		display: flex;
		min-width: 0px;
		flex-direction: column;
		align-items: flex-start;
		gap: 0.25rem;
		padding-right: 0.5rem;
		text-align: start;
}

	.gi-provider-page-link {
		color: inherit;
		text-decoration-line: none;
}

	.gi-provider-page-link:hover {
		text-decoration-line: underline;
		text-underline-offset: 2px;
}

	.gi-info-chevron {
		flex-shrink: 0;
		--tw-text-opacity: 1;
		color: rgb(255 255 255 / var(--tw-text-opacity, 1));
		transition-property: transform;
		transition-duration: 300ms;
		transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
}

	.gi-info-chevron.rotated {
		--tw-rotate: 180deg;
		transform: translate(var(--tw-translate-x), var(--tw-translate-y)) rotate(var(--tw-rotate)) skewX(var(--tw-skew-x)) skewY(var(--tw-skew-y)) scaleX(var(--tw-scale-x)) scaleY(var(--tw-scale-y));
}

	.gi-info-dropdown-menu {
		position: relative;
		margin-top: -1.5rem;
		width: 100%;
		overflow: hidden;
		border-radius: 0.5rem;
		background-color: transparent;
		padding-left: 0px;
		padding-right: 0px;
		padding-top: 1.25rem;
		--tw-text-opacity: 1;
		color: rgb(255 255 255 / var(--tw-text-opacity, 1));
		transition-property: all;
		transition-duration: 150ms;
		transition-duration: 500;
		transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
		max-height: 0;
}

	.gi-info-dropdown-menu.expanded {
		max-height: 10000px;
	}

	.gi-info-dropdown-inner {
		position: relative;
		margin-left: auto;
		margin-right: auto;
		width: 100%;
		padding: 1rem;
}

	.game-info-provider-name {
		font-weight: 400;
}

	.game-info-by-prefix {
		opacity: 0.7;
}

	.game-info-settings-group {
		display: flex;
		align-items: center;
		gap: 1.5rem;
}

	.game-info-like-fav {
		display: flex;
		align-items: center;
		gap: 0.25rem;
		font-size: 0.75rem;
		line-height: 1rem;
		--tw-text-opacity: 1;
		color: rgb(255 255 255 / var(--tw-text-opacity, 1));
}

	.game-info-fav-like-count {
		min-width: 1ch;
		font-weight: 500;
		--tw-numeric-spacing: tabular-nums;
		font-variant-numeric: var(--tw-ordinal) var(--tw-slashed-zero) var(--tw-numeric-figure) var(--tw-numeric-spacing) var(--tw-numeric-fraction);
		--tw-text-opacity: 1;
		color: rgb(255 255 255 / var(--tw-text-opacity, 1));
}

	.game-info-icon-button {
		position: relative;
		margin: 0px;
		display: flex;
		height: 24px;
		width: 24px;
		cursor: pointer;
		align-items: center;
		justify-content: center;
		border-style: none;
		background-color: transparent;
		padding: 0px;
}

	.game-description {
		position: relative;
		z-index: 30;
		display: flex;
		width: 100%;
		flex-direction: column;
		gap: 1rem;
}

	.game-info-markdown {
		width: 100%;
}

	.review-content {
		display: flex;
		flex-direction: column;
		gap: 1rem;
}

	.review-cards-row {
		display: flex;
		width: 100%;
		flex-direction: column;
		gap: 1rem;
}

	@media (min-width: 1024px) {
		.review-cards-row {
				flex-direction: row;
		}
		.review-cards-row > :global(*:first-child) {
				width: 30%;
		}
		.review-cards-row > :global(*:last-child) {
				flex: 1 1 0%;
		}
}

	:global(.game-info-markdown h1),
	:global(.game-info-markdown h2),
	:global(.game-info-markdown h3),
	:global(.game-info-markdown h4),
	:global(.game-info-markdown h5),
	:global(.game-info-markdown h6) {
		margin-bottom: 2rem;
		font-size: 24px;
		font-weight: 700;
		line-height: 30px;
}

	:global(.game-info-markdown p),
	:global(.game-info-markdown ul),
	:global(.game-info-markdown ol) {
		margin-bottom: 2rem;
		font-size: 14px;
		font-weight: 100;
}

	:global(.game-info-markdown li) {
		margin-bottom: 1rem;
}

	:global(.game-info-markdown hr) {
		margin-bottom: 2rem;
		height: 1px;
		border-color: var(--color-neutral-900);
		background-color: var(--color-neutral-900);
}

	/* Outer clips rounded corners; inner keeps horizontal scroll (border-collapse ignores rounded on <table>) */
	:global(.game-info-markdown-table-outer) {
		margin-bottom: 1rem;
		max-width: 100%;
		overflow: hidden;
		border-radius: 12px;
}

	:global(.game-info-markdown-table-inner) {
		max-width: 100%;
		overflow-x: auto;
}

	:global(.game-info-markdown table) {
		margin-bottom: 0px;
		width: 100%;
		border-collapse: collapse;
}

	:global(.game-info-markdown th),
	:global(.game-info-markdown td) {
		padding-left: 1rem;
		padding-right: 1rem;
		padding-top: 0.5rem;
		padding-bottom: 0.5rem;
		text-align: left;
		background: var(--color-background-100);
}

	:global(.game-info-markdown th) {
		font-size: 12px;
		font-weight: 700;
		color: var(--color-text-300);
}

	:global(.game-info-markdown td) {
		font-size: 12px;
		font-weight: 100;
}
</style>
